import React, { useEffect, useState } from 'react';
import { analyticsAPI, handleApiError } from '../services/api';
import StatCard, { StatGrid } from '../components/StatCard';
import DailyBars from '../components/DailyBars';
import { formatDate } from '../utils/formatters';
import type { ActivitySummary, ActivitySurface } from '../types';

const SURFACES: { key: ActivitySurface; label: string; icon: string }[] = [
  { key: 'ios', label: 'iOS app', icon: 'fab fa-apple' },
  { key: 'android', label: 'Android app', icon: 'fab fa-android' },
  { key: 'web', label: 'vozyme.eu', icon: 'fas fa-globe' },
  { key: 'pwa', label: 'app.vozyme.eu (PWA)', icon: 'fas fa-mobile-alt' },
];

interface ActivitySectionProps {
  /** A new value reloads the section, so it follows the dashboard refresh. */
  refreshKey?: unknown;
}

/** Daily users per surface and anonymous web visitors (GET /analytics/activity). */
const ActivitySection: React.FC<ActivitySectionProps> = ({ refreshKey }) => {
  const [data, setData] = useState<ActivitySummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    analyticsAPI
      .getActivity(30)
      .then((response) => {
        if (cancelled) return;
        setData(response.data);
        setError(null);
      })
      .catch((err) => {
        if (!cancelled) setError(handleApiError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return (
    <div className="section">
      <h3 className="section-title">
        <i className="fas fa-signal"></i>
        Активност по дни
      </h3>
      {error && <p className="activity-note">Активността не се зареди: {error}</p>}
      {!data && !error && (
        <StatGrid>
          <StatCard label="" loading />
          <StatCard label="" loading />
          <StatCard label="" loading />
          <StatCard label="" loading />
        </StatGrid>
      )}
      {data && (
        <>
          <StatGrid>
            <StatCard label="Активни днес (DAU)" icon="fas fa-user-check" tone="brand" value={data.dau.value} change={data.dau} />
            <StatCard
              label="Активни 7 дни (WAU)"
              icon="fas fa-calendar-week"
              tone="brand"
              value={data.wau.value}
              change={data.wau}
              changeLabel="от предходните 7 дни"
            />
            <StatCard label="Активни 30 дни (MAU)" icon="fas fa-calendar-alt" tone="brand" value={data.mau} />
            <StatCard
              label="DAU / MAU"
              icon="fas fa-magnet"
              tone="brand"
              value={data.stickiness_percent === null ? '–' : `${data.stickiness_percent}%`}
              hint="Колко от месечните идват днес"
            />
            <StatCard
              label="Анонимни днес"
              icon="fas fa-user-secret"
              tone="neutral"
              value={data.visitors_today.value}
              change={data.visitors_today}
            />
            <StatCard
              label="Анонимни 7 дни"
              icon="fas fa-eye"
              tone="neutral"
              value={data.visitor_days_7d}
              hint="Сбор по дни: един човек за 3 дни е 3"
            />
          </StatGrid>

          <h4 className="activity-subtitle">Активни за 7 дни по канал</h4>
          <StatGrid>
            {SURFACES.map((s) => (
              <StatCard key={s.key} label={s.label} icon={s.icon} tone="brand" value={data.users_7d_by_surface[s.key]} />
            ))}
            {data.users_7d_by_surface.other > 0 && (
              <StatCard
                label="Друг клиент"
                icon="fas fa-question-circle"
                tone="warning"
                value={data.users_7d_by_surface.other}
                hint="Непознат клиент: виж User-Agent в логовете"
              />
            )}
          </StatGrid>

          <div className="activity-charts">
            <DailyBars
              title="Активни потребители по дни"
              points={data.days.map((d) => ({ date: d.date, value: d.users }))}
            />
            <DailyBars
              title="Анонимни посетители по дни"
              points={data.days.map((d) => ({ date: d.date, value: d.visitors }))}
            />
          </div>
          <p className="activity-note">
            Ден по UTC. Броим от {data.tracking_since ? formatDate(data.tracking_since) : 'днес'}; по-старите дни са 0.
            Анонимните посетители се броят без бисквитка: хеш от IP и браузър с ключ, който се сменя всеки ден.
          </p>
        </>
      )}
    </div>
  );
};

export default ActivitySection;
